import React from 'react'
import { i18nTranslate, convertToBoolean } from 'src/utils'
import { observable, reaction } from 'mobx'
import { observer } from 'mobx-react'
import Slider from 'react-slick'
import TagManager from 'react-gtm-module'
import { Button } from 'react-bootstrap'
import {
  IS_BROWSER,
  APPConfig,
  convertToBoolean as convertToBooleanApp,
} from 'config/appConfig'
import NuskinArrowRight from 'src/views/components/Icons/NuskinArrowRight'
import NuskinArrowLeft from 'src/views/components/Icons/NuskinArrowLeft'
import ProductImageGallery from '../ProductImageGallery/ProductImageGallery'
import { ImageComponent } from 'src/views/components'
import {
  YOUTUBE_ID_REGEXP,
  VIDEO_URL_REGEXP,
  extractYouTubeId,
} from 'src/utils/mediaUtils'
import { productDeps } from 'src/deps'
import './styles.scss'

@observer
class ProductImageCarousel extends React.Component {
  @observable carouselNavs = {
    nav1: null,
    nav2: null,
    currentSlideIndex: 0,
  }

  constructor(props) {
    super(props)
  }

  getReactionKey = () => {
    const product = this.props?.product || {}
    const skuSelected = product?.skuSelected || {}
    const mandatorySelectedSkus = (product?.bundleMandatoryProducts || [])
      .map(item => item?.selectedSku?.identifier || '')
      .join('|')

    return JSON.stringify({
      skuSelected,
      mandatorySelectedSkus,
      lastSelectedBundleSubProductIndex:
        product?.lastSelectedBundleSubProductIndex || 0,
    })
  }

  getBundleSelectedSubProduct = product => {
    const mandatoryProducts = product?.bundleMandatoryProducts || []
    if (!mandatoryProducts.length) {
      return null
    }

    const selectedIndex = Number(product?.lastSelectedBundleSubProductIndex)
    if (
      Number.isInteger(selectedIndex) &&
      selectedIndex >= 0 &&
      selectedIndex < mandatoryProducts.length
    ) {
      return mandatoryProducts[selectedIndex]
    }

    return mandatoryProducts[0]
  }

  getBundleDrivenImageSource = product => {
    // Only override the main image once the user has explicitly selected a
    // sub-product variant. On initial PDP load the index is unset, so the
    // bundle-level primaryimage is used and matches what the PLP shows.
    const hasSelectedSubProduct =
      product?.lastSelectedBundleSubProductIndex !== undefined &&
      product?.lastSelectedBundleSubProductIndex !== null

    if (
      (!productDeps.isBundleData(product) && product?.type !== 'kit') ||
      !hasSelectedSubProduct
    ) {
      return {
        imageList: product?.productImages || [],
        primaryImage: product?.primaryimage || '',
      }
    }

    const selectedSubProduct = this.getBundleSelectedSubProduct(product)
    const selectedSkuProperties =
      selectedSubProduct?.selectedSku?.properties || {}

    return {
      imageList: product?.productImages || [],
      primaryImage:
        selectedSkuProperties?.primaryimage ||
        selectedSkuProperties?.imageURL ||
        product?.primaryimage ||
        '',
    }
  }

  componentDidMount() {
    this.carouselNavs = {
      nav1: this.slider1,
      nav2: this.slider2,
      currentSlideIndex: 0,
    }
    reaction(
      () => {
        try {
          return this.getReactionKey()
        } catch (e) {
          console.log('Data parse error::: ProductImageCarousel' + e)
        }
      },
      () => {
        setTimeout(() => {
          this.carouselNavs.currentSlideIndex = 0
          this.carouselNavs.nav1?.slickGoTo(0)
          this.carouselNavs.nav2?.slickGoTo(0)
        }, 600)
      }
    )
  }

  isYouTubeVideo = (videoUrl = '') => {
    if (!videoUrl) return false
    const match = videoUrl.match(YOUTUBE_ID_REGEXP)
    return !!(match && match[1] && match[1].length === 11)
  }

  methodToReuseAdaFix = () => {
    if (IS_BROWSER) {
      document
        .querySelectorAll(`#pdp-content div[aria-hidden=false] `)
        .forEach(anchorTag => anchorTag.removeAttribute('tabindex', '-1'))

      document
        .querySelectorAll(
          `#pdp-content div[aria-hidden=false] .thumbnailSlider`
        )
        .forEach(anchorTag => anchorTag.removeAttribute('tabindex', '-1'))
    }
  }
  handleTabIndex = () => {
    this.methodToReuseAdaFix()
  }

  pauseVideo = () => {
    document
      .querySelectorAll('.productcarousel video')
      .forEach(video => video.pause())
  }

  handleTabIndexAndGTMCarouselClick = () => {
    this.pauseVideo()
    this.methodToReuseAdaFix()
    TagManager.dataLayer({
      dataLayer: {
        event: 'carouselClicked',
        pagePath: `/product/:id`,
        pageTitle: 'Product Details Page',
        section: 'Carousel in Product Details Page',
        platform: 'equinox',
      },
    })
  }

  handlePDPCarouselVideoPlay = () => {
    const product = this.props?.product || {}
    const { name, productId, skuId } = product

    TagManager.dataLayer({
      dataLayer: {
        event: 'videoPlayed',
        name: name || '',
        productId: productId || '',
        skuId: skuId || '',
        pagePath: `/product/:id`,
        pageTitle: 'Product Details Page',
        platform: 'equinox',
      },
    })
  }
  getCustomDots = (productImageList = [], dots) => {
    const customDots = []
    productImageList?.forEach((block, index) => {
      customDots.push(
        <li key={`dot-${index}`} {...dots?.[index]?.props}>
          <button
            aria-label={this.getDotsAriaLabel({ productImageList, index })}
            onClick={dots?.[index]?.props?.children?.props?.onClick}>
            {index + 1}
          </button>
        </li>
      )
    })

    return <ul className="slick-dots">{customDots}</ul>
  }

  getDotsAriaLabel = props => {
    const { productImageList, index } = props || {}
    const selectedTxt =
      this.carouselNavs.currentSlideIndex == index
        ? i18nTranslate('pdp.selected', `, selected `, {
            nameSpace: 'ssr-resource',
          })
        : ''
    return (
      i18nTranslate('pdp.CarouselDots', `product image `, {
        nameSpace: 'ssr-resource',
      }) +
      parseInt(index + 1) +
      i18nTranslate('pdp.of', ` of `, {
        nameSpace: 'ssr-resource',
      }) +
      productImageList?.length +
      selectedTxt
    )
  }

  getMedia = mediaProps => {
    const {
      url = '',
      alt = i18nTranslate('pdp.defaultAlt', 'Product carousel image', {
        nameSpace: 'ssr-resource',
      }),
      index,
      isToShowControls = true,
      ref,
      isFromThumbnail = false,
      customClassName = '',
      customParentClassName = '',
      overrideParentClass = false,
      loadingType = 'eager',
    } = mediaProps
    const videoUrlRegEx = VIDEO_URL_REGEXP
    if (this.isYouTubeVideo(url)) {
      const videoId = extractYouTubeId(url) || ''
      return (
        <iframe
          tabIndex={index}
          width="100%"
          className="media"
          src={`https://www.youtube.com/embed/${videoId}?controls=${
            isToShowControls ? '0' : '1'
          }`}
        />
      )
    } else if (url.match(videoUrlRegEx)) {
      return (
        <video
          onPlay={this.handlePDPCarouselVideoPlay}
          tabIndex={index}
          width="100%"
          controls={isToShowControls}>
          <source src={url} />
        </video>
      )
    }
    return (
      <ImageComponent
        tabIndex={index}
        className={`media ${customClassName}`}
        customParentClassName={customParentClassName}
        overrideParentClass={overrideParentClass}
        src={url}
        alt={alt}
        optimizeImage={isFromThumbnail}
        width={isFromThumbnail ? '44px' : 'auto'}
        loadingType={loadingType}
      />
    )
  }

  render() {
    const renderThumbnailImage = () => {
      const parsedImageList = (() => {
        try {
          return typeof imageList === 'string'
            ? JSON.parse(imageList)
            : imageList
        } catch (error) {
          console.error('Failed to parse imageList:', error)
          return []
        }
      })()
      return (
        <>
          {parsedImageList && parsedImageList.length > 0 ? (
            <Slider
              className="product-thubnailSlider mt-3"
              asNavFor={this.carouselNavs?.nav1}
              ref={slider => (this.slider2 = slider)}
              slidesToShow={parsedImageList?.length || 0}
              variableWidth={true}
              slidesToScroll={1}
              swipeToSlide={true}
              arrows={false}
              infinite={true}
              afterChange={index =>
                (this.carouselNavs.currentSlideIndex = index)
              }
              focusOnSelect={true}>
              {parsedImageList?.map((image, index) => {
                return (
                  <Button
                    key={index}
                    className="thumbnailSlider productcarousel_slider"
                    aria-label={this.getDotsAriaLabel({
                      imageList,
                      index,
                    })}
                    data-testid={`qa-pdp-thumbnail-image-${index + 1}`}>
                    {this.getMedia({
                      url: image?.thumbnail || image || '',
                      alt: image?.alt || productName || '',
                      loadingType: 'eager',
                      isToShowControls: false,
                      isFromThumbnail: false,
                      index,
                    })}
                  </Button>
                )
              })}
            </Slider>
          ) : (
            <>
              {this.getMedia({
                url: product?.primaryimage || '',
                alt: productName || 'thumbnail image',
                isToShowControls: false,
                isFromThumbnail: true,
                loadingType: 'eager',
                customClassName: 'bundle_image_thumbnail',
                customParentClassName: 'w-100',
                overrideParentClass: true,
              })}
            </>
          )}
        </>
      )
    }
    const renderProductImage = () => {
      return (
        <>
          {productImageList && productImageList.length > 0 ? (
            <Slider
              {...settings}
              asNavFor={this.carouselNavs?.nav2}
              ref={slider => (this.slider1 = slider)}>
              {productImageList?.map((image, index) => {
                return (
                  <div
                    key={`productcarousel-${index}`}
                    role="img"
                    aria-live="polite"
                    aria-label={this.getDotsAriaLabel({
                      productImageList,
                      index,
                    })}
                    className={`productcarousel_slider ${
                      isReplacementProductStatus
                        ? 'discontinue-slider discontinued'
                        : ''
                    }`}>
                    {isReplacementProductStatus && (
                      <div className="discontinued-text">
                        {i18nTranslate(
                          'pdp.ItemDiscontinued',
                          'Item Discontinued',
                          {
                            nameSpace: 'ssr-resource',
                          }
                        )}
                      </div>
                    )}
                    {this.getMedia({
                      url: image?.url || image || '',
                      alt: image?.alt || productName || '',
                      index,
                      loadingType: index == 0 ? 'eager' : 'lazy',
                    })}
                  </div>
                )
              })}
            </Slider>
          ) : (
            <>
              <div
                className={`${
                  isReplacementProductStatus
                    ? 'discontinue-slider discontinued'
                    : ''
                }`}>
                {isReplacementProductStatus && (
                  <div className="discontinued-text">
                    {i18nTranslate(
                      'pdp.ItemDiscontinued',
                      'Item Discontinued',
                      {
                        nameSpace: 'ssr-resource',
                      }
                    )}
                  </div>
                )}
                {this.getMedia({
                  url: product?.primaryimage || '',
                  alt: productName || '',
                  loadingType: 'eager',
                  customClassName: 'bundle_image',
                })}
              </div>
            </>
          )}
          <div className="express-image-count text-center f-14 mt-4">
            {this.carouselNavs?.currentSlideIndex +
              1 +
              ' / ' +
              productImageList?.length || 1}
          </div>
        </>
      )
    }
    const product = this.props?.product || {}
    const selectedSku =
      product?.selectedSku ||
      productDeps.getUserSelectedSku({
        sku: product?.sku,
        skuId: product?.skuId,
      })
    const isproductStatus =
      selectedSku?.properties?.productStatus?.toLowerCase() == 'discontinued'
    let isBundleProduct = productDeps.isBundleData(product)
    const enableDiscontinuedSKUReplacement =
      convertToBoolean(
        APPConfig?.getAppConfig()?.enableDiscontinuedSKUReplacement
      ) || false
    const isReplacementProductStatus =
      Array.isArray(product?.replacementProduct) &&
      product?.replacementProduct?.every(
        item => typeof item === 'object' && item !== null
      ) &&
      isproductStatus &&
      !isBundleProduct &&
      enableDiscontinuedSKUReplacement

    const { imageList, primaryImage } = this.getBundleDrivenImageSource(product)
    const productName = product.name || ''
    let productImageList = [product?.primaryimage]
    try {
      productImageList = (imageList && JSON.parse(imageList)) || []
    } catch (e) {}

    const mainImageOverride = primaryImage || product?.primaryimage || ''

    const nextIcon = (
      <svg
        stroke="currentColor"
        fill="none"
        strokeWidth="0"
        viewBox="0 0 24 24"
        data-role="none"
        className="d-block"
        currentSlide="0"
        slideCount="7"
        height="1em"
        width="1em">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M9 5l7 7-7 7"></path>
      </svg>
    )
    const prevIcon = (
      <svg
        stroke="currentColor"
        fill="none"
        strokeWidth="0"
        viewBox="0 0 24 24"
        data-role="none"
        className="d-block"
        currentSlide="0"
        slideCount="7"
        height="1em"
        width="1em">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M15 19l-7-7 7-7"></path>
      </svg>
    )
    const settings = {
      dots: false,
      infinite: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      onInit: this.handleTabIndex,
      afterChange: this.handleTabIndexAndGTMCarouselClick,
      nextArrow: (
        <NuskinArrowRight customIcon={nextIcon} styles={{ width: '1.5rem' }} />
      ),
      prevArrow: (
        <NuskinArrowLeft customIcon={prevIcon} styles={{ width: '1.5rem' }} />
      ),
      responsive: [
        {
          breakpoint: this.props?.isExpressPDP ? 786 : 389,
          settings: {
            arrows: false,
            dots: true,
            appendDots: dots => this.getCustomDots(productImageList, dots),
          },
        },
      ],
    }

    // Always use ProductImageGallery for both legacy and redesign
    return (
      <div>
        <ProductImageGallery
          data-testid="qa-pdp-product-image-carousel"
          productImageList={productImageList}
          mainImageOverride={mainImageOverride}
        />
      </div>
    )
  }
}

export { ProductImageCarousel }
